import { isNaN, pick, size } from 'lodash-es';

import {
  formatAmount,
  formatDate,
  formatVehicleStage,
  generateShortId,
  getCleanedUrlParams,
} from '@utils';

import {
  AVAILABILITY_STATUSES,
  BOARDS_ID,
  RESERVATIONS_TYPES,
  TENANT_NAMES,
  BOARDS_ENUMS,
  BOARDS_ID_TO_URLS_MAP,
  FILTER_RULE_ENUMS,
  FILTER_OPERATOR_ENUMS,
  FILTER_FIELDS_TYPES,
} from '@constants';

import { FLEET_VEHICLES_TABS } from 'components/vehicles/FleetVehiclesFilters/constants';
import { ARNM_ACCIDENTS_TASK_STATUSES_NAMES } from 'components/arnmAccidents/constants';
import { ARNM_REPAIRS_TASK_STATUSES_NAMES } from 'components/arnmRepairs/constants';
import { ARNM_MAINTENANCE_TASK_STATUSES_NAMES } from 'components/arnmMaintenance/constants';
import { AVAILABLE_HISTORY_ITEMS } from 'components/common/InstaTaskHistory/constants';

import {
  financingTypeOptions,
  vehicleOwnershipOptions,
} from 'components/vehicles/FleetVehiclesGrid/constants';
import { URLS } from 'layouts/DrawerLayout/constants';
import { computeValueByRuleType } from 'components/common/modals/FiltersModalV2/utils';

import {
  DEFAULT_LOCATIONS,
  DEFLEETED_PRIORITIES,
  DELAY_REASONS,
  invoiceDetailsFields,
  procurementFields,
  propertiesFields,
  topLevelFields,
  VEHICLE_DEFAULT_PRICING,
  VEHICLE_FINANCING_TYPES,
  VEHICLE_OWNERSHIPS,
  VEHICLES_PAGE_SIZE,
} from './constants';

const isSystemActor = (actor) => {
  if (!actor) return true;

  const normalizedActor = actor.trim().toLowerCase();

  return (
    normalizedActor === 'system' ||
    normalizedActor === 'pipedrive' ||
    normalizedActor.startsWith('internal')
  );
};

const europeanDateFormatter = new Intl.DateTimeFormat('el-GR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const europeanMonthOfDefleetDateFormatter = new Intl.DateTimeFormat('el-GR', {
  month: '2-digit',
  year: 'numeric',
});

const getDataMap = (data = []) =>
  data.reduce((acc, cur) => {
    acc[cur.id || cur.value] = { ...cur, name: cur.name || cur.text };
    return acc;
  }, {});

const getDataOptions = ({ map, options, key }) => {
  const value = map[key];
  let finalOptions = [...options];
  if (key && !value) {
    finalOptions = [
      {
        label: key,
        value: key,
      },
      ...finalOptions,
    ];
  }
  return finalOptions;
};

export const transformVehiclesToGridRows = ({
  vehicles,
  enums,
  servicePoints,
  servicePointsMap,
}) => {
  const defaultOption = { label: 'None', value: '' };

  const insurancesMap = getDataMap(enums?.insurances);
  const financingMap = getDataMap(enums?.financing);
  const normalizedFinancingTypes = financingTypeOptions.map((financing) => ({
    label: financing,
    value: financing,
  }));
  const normalizedSourcingTypes = [
    defaultOption,
    ...(typeof enums.sourcingTypes[0] === 'string'
      ? enums.sourcingTypes.map((s) => ({ label: s, value: s }))
      : enums.sourcingTypes),
  ];
  const normalizedVehicleOwnershipOptions = [
    ...vehicleOwnershipOptions.map((ownership) => ({
      label: ownership,
      value: ownership,
    })),
  ];
  const sourcingTypesMap = getDataMap(normalizedSourcingTypes);
  const financingTypeMap = getDataMap(normalizedFinancingTypes);
  const vehicleOwnershipOptionsMap = getDataMap(
    normalizedVehicleOwnershipOptions
  );
  const colorsMap = enums?.colors.reduce((acc, color) => {
    if (color?.value) acc[color.value] = color;
    return acc;
  }, {});
  const dealersMap = enums?.dealers.reduce((acc, dealer) => {
    if (dealer?.value) acc[dealer.value] = dealer;
    return acc;
  }, {});

  if (!vehicles) {
    return [];
  }

  return vehicles.map((item, idx) => {
    const colorOptions = getDataOptions({
      map: colorsMap,
      options: [defaultOption, ...(enums?.colors || [])],
      key: item.properties?.color,
    });

    const dealerOptions = getDataOptions({
      map: dealersMap,
      options: [defaultOption, ...(enums?.dealers || [])],
      key: item.procurement?.dealer,
    });

    const location = servicePointsMap.get(item.location?.name);
    let locationOptions = [...servicePoints];
    if (item.location?.id && !location) {
      locationOptions = [
        { label: item.location.name, value: item.location.name },
        ...locationOptions,
      ];
    }

    const insuranceProviderOptions = getDataOptions({
      map: insurancesMap,
      options: enums?.insurances || [],
      key: item.procurement?.insuranceProvider,
    });

    const vehicleOwnerships = getDataOptions({
      map: vehicleOwnershipOptionsMap,
      options: normalizedVehicleOwnershipOptions,
      key: item.procurement?.vehicleOwnership,
    });

    const financingProviderOptions = getDataOptions({
      map: financingMap,
      options: [defaultOption, ...(enums?.financing || [])],
      key: item.procurement?.financingProvider,
    });

    const financingTypesOptions = getDataOptions({
      map: financingTypeMap,
      options: normalizedFinancingTypes,
      key: item.procurement?.financingType,
    });

    const sourcingTypesOptions = getDataOptions({
      map: sourcingTypesMap,
      options: normalizedSourcingTypes,
      key: item.procurement?.sourcingType,
    });

    return {
      rowId: idx,
      height: 32,
      cells: [
        {
          type: 'text',
          id: item.id,
          text: formatVehicleStage(item.procurement.currentStageId),
          key: 'procurementStage',
          nonEditable: true,
        },
        {
          type: 'link',
          id: item.id,
          text: item.internalId || '',
          key: 'internalId',
          nonEditable: true,
          href: `${URLS.fleetVehicles}/${item.id}`,
        },
        {
          type: 'link',
          id: item.id,
          text: item.skuId || '',
          key: 'skuId',
          nonEditable: true,
          href: `${URLS.catalogCars}/${item.skuId}/edit`,
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement.vehicleOwnership || '',
          key: 'vehicleOwnership',
          isOpen: false,
          values: vehicleOwnerships,
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.tenant.toString(),
          values: Object.keys(TENANT_NAMES).map((key) => ({
            label: TENANT_NAMES[key],
            value: key,
          })),
          key: 'tenant',
          isOpen: false,
        },
        {
          type: 'text',
          id: item.id,
          text: item.sku?.name || '',
          key: 'description',
          nonEditable: true,
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.properties?.color || '',
          key: 'color',
          isOpen: false,
          values: colorOptions,
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.condition || '',
          key: 'condition',
          isOpen: false,
          values: ['NEW', 'USED'].reduce(
            (acc, curr) => [...acc, { value: curr, label: curr }],
            []
          ),
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement?.kilometersAtPurchase ?? ''),
          value: Number(item.procurement?.kilometersAtPurchase),
          key: 'kilometersAtPurchase',
        },
        { type: 'text', id: item.id, text: item.vin || '', key: 'vin' },
        { type: 'text', id: item.id, text: item.plate || '', key: 'plate' },

        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.location?.name || '',
          values: locationOptions,
          key: 'location',
        },
        {
          type: 'text',
          id: item.id,
          text:
            Object.values(RESERVATIONS_TYPES).find(
              (type) => type.value === item.availability?.reservationType
            )?.text || '-',
          key: 'reservationType',
          nonEditable: true,
        },
        {
          type: 'text',
          id: item.id,
          text: item.procurement.supplierOrderId || '',
          key: 'supplierOrderId',
          nonEditable: true,
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.orderDate
            ? new Date(item.procurement.orderDate)
            : '',
          key: 'orderDate',
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement.dealer,
          values: dealerOptions,
          key: 'dealer',
          isOpen: false,
        },

        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.cancelledDate
            ? new Date(item.procurement.cancelledDate)
            : '',
          key: 'cancelledDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.availability?.erfudd
            ? new Date(item.availability.erfudd)
            : '',
          key: 'erfudd',
          nonEditable: true,
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement?.retailPrice?.number ?? ''),
          value: Number(item.procurement?.retailPrice?.number),
          key: 'retailPrice',
        },

        {
          type: 'number',
          id: item.id,
          text: String(item.procurement?.retailPriceBeforeTax?.number ?? ''),
          value: Number(item.procurement?.retailPriceBeforeTax?.number),
          key: 'retailPriceBeforeTax',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement?.vat?.number ?? ''),
          value: Number(item.procurement?.vat?.number),
          key: 'vat',
        },
        {
          type: 'text',
          id: item.id,
          text:
            item.procurement.extras && Array.isArray(item.procurement.extras)
              ? item.procurement.extras.join(',')
              : '',
          key: 'extras',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement?.extrasPrice?.number ?? ''),
          value: Number(item.procurement?.extrasPrice?.number),
          key: 'extrasPrice',
        },

        {
          type: 'number',
          id: item.id,
          text: String(
            item.procurement?.specialClassificationFee?.number ?? ''
          ),
          value: Number(item.procurement?.specialClassificationFee?.number),
          key: 'specialClassificationFee',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement?.discountPercentage ?? ''),
          value: Number(item.procurement?.discountPercentage),
          key: 'discountPercentage',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement.discountWithoutVat?.number ?? ''),
          value: Number(item.procurement.discountWithoutVat?.number),
          key: 'discountWithoutVat',
        },

        {
          type: 'number',
          id: item.id,
          text: String(item.procurement.agreedPurchasePrice?.number ?? ''),
          value: Number(item.procurement.agreedPurchasePrice?.number),
          key: 'agreedPurchasePrice',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement.finalPurchasePrice?.number ?? ''),
          value: Number(item.procurement.finalPurchasePrice?.number),
          key: 'finalPurchasePrice',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.agreedCustomsDate
            ? new Date(item.procurement.agreedCustomsDate)
            : '',
          key: 'agreedCustomsDate',
        },

        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.estimatedCustomsDate
            ? new Date(item.procurement.estimatedCustomsDate)
            : '',
          key: 'estimatedCustomsDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.customsEntryDate
            ? new Date(item.procurement.customsEntryDate)
            : '',
          key: 'customsEntryDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.plannedDownpaymentDate
            ? new Date(item.procurement.plannedDownpaymentDate)
            : '',
          key: 'plannedDownpaymentDate',
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement.delayReason || '',
          isOpen: false,
          key: 'delayReason',
          values: DELAY_REASONS,
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.licenseIssuanceDate
            ? new Date(item.procurement.licenseIssuanceDate)
            : '',
          key: 'licenseIssuanceDate',
        },

        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement?.insuranceProvider || '',
          key: 'insuranceProvider',
          isOpen: false,
          values: [defaultOption, ...(insuranceProviderOptions || [])],
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.insuranceDate
            ? new Date(item.procurement.insuranceDate)
            : '',
          key: 'insuranceDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.insuranceRenewalDate
            ? new Date(item.procurement.insuranceRenewalDate)
            : '',
          key: 'insuranceRenewalDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.plannedReceiptDate
            ? new Date(item.procurement.plannedReceiptDate)
            : '',
          key: 'plannedReceiptDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.receiptDate
            ? new Date(item.procurement.receiptDate)
            : '',
          key: 'receiptDate',
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement.sourcingType || '',
          key: 'sourcingType',
          isOpen: false,
          values: sourcingTypesOptions,
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement.financingType || '',
          key: 'financingType',
          isOpen: false,
          values: financingTypesOptions,
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: item.procurement.financingProvider || '',
          key: 'financingProvider',
          isOpen: false,
          values: financingProviderOptions,
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.vehiclePurchaseDate
            ? new Date(item.procurement.vehiclePurchaseDate)
            : '',
          key: 'vehiclePurchaseDate',
        },
        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.leaseStartDate
            ? new Date(item.procurement.leaseStartDate)
            : '',
          key: 'leaseStartDate',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.procurement.leaseMonthlyInstallment?.number ?? ''),
          value: Number(item.procurement.leaseMonthlyInstallment?.number),
          key: 'leaseMonthlyInstallment',
        },

        {
          type: 'date',
          format: europeanDateFormatter,
          id: item.id,
          date: item.procurement.defleetedDate
            ? new Date(item.procurement.defleetedDate)
            : '',
          key: 'defleetedDate',
        },
        {
          type: 'dropdown',
          id: item.id,
          selectedValue: String(item.procurement?.defleetPriority ?? ''),
          key: 'defleetPriority',
          isOpen: false,
          values: DEFLEETED_PRIORITIES,
        },
        {
          type: 'date',
          format: europeanMonthOfDefleetDateFormatter,
          id: item.id,
          date: item.procurement.monthOfDefleet
            ? new Date(item.procurement.monthOfDefleet)
            : '',
          key: 'monthOfDefleet',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.invoiceDetails?.invoiceNumber ?? ''),
          value: Number(item.invoiceDetails?.invoiceNumber),
          key: 'invoiceNumber',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.invoiceDetails?.netPrice?.number ?? ''),
          value: Number(item.invoiceDetails?.netPrice?.number),
          key: 'netPrice',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.invoiceDetails?.registrationTax?.number ?? ''),
          value: Number(item.invoiceDetails?.registrationTax?.number),
          key: 'registrationTax',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.invoiceDetails?.vat?.number ?? ''),
          value: Number(item.invoiceDetails?.vat?.number),
          key: 'invoiceDetailsVat',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.invoiceDetails?.transferFee?.number ?? ''),
          value: Number(item.invoiceDetails?.transferFee?.number),
          key: 'transferFee',
        },
        {
          type: 'number',
          id: item.id,
          text: String(item.invoiceDetails?.plateReleaseFee?.number ?? ''),
          value: Number(item.invoiceDetails?.plateReleaseFee?.number),
          key: 'plateReleaseFee',
        },
        {
          type: 'text',
          id: item.id,
          text:
            enums?.categoryClasses.find(
              (cls) => cls.value === item.sku?.categoryClass
            )?.text ?? '',
          key: 'categoryClass',
          nonEditable: true,
        },
      ],
    };
  });
};

export const handleEnums = (results = {}) => {
  const dealers = (results?.dealers?.data || []).map((item) => ({
    value: item.slug,
    label: item.name,
  }));
  const colors = (results?.colors?.data || []).map((item) => ({
    value: item.slug,
    label: item.name,
  }));
  const insurances = (results?.insurances?.data || []).map((item) => ({
    value: item.name,
    label: item.name,
  }));
  const financing = Object.keys(
    results?.vehiclesEnums?.data?.financingProviders || {}
  )?.map((item) => ({
    value: results?.vehiclesEnums?.data?.financingProviders[item].name,
    label: results?.vehiclesEnums?.data?.financingProviders[item].name,
  }));
  const categoryClasses = Object.keys(
    results?.vehiclesEnums?.data?.categoryClasses || {}
  )?.map((item) => ({
    value: results?.vehiclesEnums?.data?.categoryClasses[item].slug,
    text: results?.vehiclesEnums?.data?.categoryClasses[item].name,
  }));

  const procurementStageIds = Object.keys(
    results?.vehiclesEnums?.data?.procurementStageIds || {}
  )?.map((item) => ({
    value: results?.vehiclesEnums?.data?.procurementStageIds[item].id,
    text: results?.vehiclesEnums?.data?.procurementStageIds[item].name,
  }));
  return {
    sourcingTypes: results?.sourcingTypes?.data || [],
    dealers,
    colors,
    insurances,
    financing,
    categoryClasses,
    procurementStageIds,
  };
};

const normalizeColors = (data = []) => {
  if (!data?.length > 0) {
    return [];
  }

  return data
    ?.map((item) => ({ value: item.name.trim(), text: item.name.trim() }))
    ?.sort((a, b) => a.value.localeCompare(b.value));
};

const normalizeDealers = (data = []) => {
  if (!data?.length > 0) {
    return [];
  }

  return data
    ?.map((item) => ({
      value: item.slug,
      text: item.name,
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));
};

const normalizeVehicleSourcingTypes = (data = []) => {
  if (!data?.length > 0) {
    return [];
  }

  return data
    ?.map((item) => ({
      value: item,
      text: item,
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));
};

const normalizeServicePoints = (data = []) => {
  const servicePoints = data
    ?.map((item) => ({
      text: item.name,
      label: item.name,
      value: item.id,
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));

  return [...DEFAULT_LOCATIONS, ...servicePoints];
};

const normalizedChangelogValues = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'empty';
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return value.toString();
};

const getServicePointsMap = (data = []) =>
  new Map(data.map(({ value, ...rest }) => [value, rest]));

const handleChangeLog = ({
  changelog,
  servicePoints,
  insurances,
  roadsideAssistance,
}) => {
  const servicePointsMap = getDataMap([...DEFAULT_LOCATIONS, ...servicePoints]);

  const insurancesMap = getDataMap(insurances);

  const roadsideAssistanceMap = getDataMap(roadsideAssistance);

  return changelog.map((log) => {
    if (
      log.field.includes('location') ||
      log.field.includes('CurrentLocation')
    ) {
      const fromLocation = servicePointsMap[log.from];
      const toLocation = servicePointsMap[log.to];
      return {
        ...log,
        from: fromLocation ? fromLocation.name : log.from,
        to: toLocation ? toLocation.name : log.to,
      };
    }
    if (log.field.includes('CurrentStageId')) {
      return {
        ...log,
        from: formatVehicleStage(log.from),
        to: formatVehicleStage(log.to),
      };
    }
    if (log.field.includes('InsuranceID')) {
      return {
        ...log,
        from: insurancesMap[log.from]?.name || 'empty',
        to: insurancesMap[log.to]?.name || 'empty',
      };
    }
    if (log.field.includes('RoadsideAssistanceID')) {
      return {
        ...log,
        from: roadsideAssistanceMap[log.from]?.name || 'empty',
        to: roadsideAssistanceMap[log.to]?.name || 'empty',
      };
    }
    if (log.field.includes('ReservationType')) {
      const from =
        Object.values(RESERVATIONS_TYPES).find(
          (item) => item.value === log.from
        )?.text || 'unknown';
      const to =
        Object.values(RESERVATIONS_TYPES).find((item) => item.value === log.to)
          ?.text || 'unknown';

      return {
        ...log,
        from: !log.from ? 'empty' : from,
        to: !log.to ? 'empty' : to,
      };
    }
    if (log.field.includes('AvailabilityStatus')) {
      const from =
        Object.values(AVAILABILITY_STATUSES).find(
          (item) => item.value === log.from
        )?.text || 'unknown';
      const to =
        Object.values(AVAILABILITY_STATUSES).find(
          (item) => item.value === log.to
        )?.text || 'unknown';

      return {
        ...log,
        from: !log.from ? 'empty' : from,
        to: !log.to ? 'empty' : to,
      };
    }
    return log;
  });
};

const handleChangelogItemFieldName = (
  log,
  servicePoints = [],
  insurances = [],
  roadsideAssistance = []
) => {
  const servicePointsMap = getDataMap([...DEFAULT_LOCATIONS, ...servicePoints]);
  const insurancesMap = getDataMap(insurances);
  const roadsideAssistanceMap = getDataMap(roadsideAssistance);

  const fieldName = log?.fieldName;

  const valueFrom = log?.valueFrom;

  const valueTo = log?.valueTo;

  if (!fieldName || typeof fieldName !== 'string') {
    return log;
  }

  // Locations
  if (fieldName.includes('location') || fieldName.includes('CurrentLocation')) {
    const fromLocation = servicePointsMap[valueFrom];
    const toLocation = servicePointsMap[valueTo];

    return {
      ...log,
      valueFrom: fromLocation ? fromLocation.name : valueFrom,
      valueTo: toLocation ? toLocation.name : valueTo,
    };
  }

  // Stage
  if (fieldName.includes('CurrentStageId')) {
    return {
      ...log,
      valueFrom: formatVehicleStage(valueFrom),
      valueTo: formatVehicleStage(valueTo),
    };
  }

  // Insurance
  if (fieldName.includes('InsuranceID')) {
    return {
      ...log,
      valueFrom: insurancesMap[valueFrom]?.name || 'empty',
      valueTo: insurancesMap[valueTo]?.name || 'empty',
    };
  }

  // Roadside Assistance
  if (fieldName.includes('RoadsideAssistanceID')) {
    return {
      ...log,
      valueFrom: roadsideAssistanceMap[valueFrom]?.name || 'empty',
      valueTo: roadsideAssistanceMap[valueTo]?.name || 'empty',
    };
  }

  // Reservation Type
  if (fieldName.includes('ReservationType')) {
    const fromReservation =
      Object.values(RESERVATIONS_TYPES).find((item) => item.value === valueFrom)
        ?.text || 'unknown';

    const toReservation =
      Object.values(RESERVATIONS_TYPES).find((item) => item.value === valueTo)
        ?.text || 'unknown';

    return {
      ...log,
      valueFrom: !valueFrom ? 'empty' : fromReservation,
      valueTo: !valueTo ? 'empty' : toReservation,
    };
  }

  // Availability Status
  if (fieldName.includes('AvailabilityStatus')) {
    const fromStatus =
      Object.values(AVAILABILITY_STATUSES).find(
        (item) => item.value === valueFrom
      )?.text || 'unknown';

    const toStatus =
      Object.values(AVAILABILITY_STATUSES).find(
        (item) => item.value === valueTo
      )?.text || 'unknown';

    return {
      ...log,
      valueFrom: !valueFrom ? 'empty' : fromStatus,
      valueTo: !valueTo ? 'empty' : toStatus,
    };
  }

  // Date
  if (fieldName.includes('Date')) {
    const fromDate = formatDate(valueFrom);

    const toDate = formatDate(valueTo);

    return {
      ...log,
      valueFrom: !valueFrom ? 'empty' : fromDate,
      valueTo: !valueTo ? 'empty' : toDate,
    };
  }

  return {
    ...log,
    valueFrom: normalizedChangelogValues(log?.valueFrom),
    valueTo: normalizedChangelogValues(log?.valueTo),
  };
};

const handleServicePointsAll = (servicePointsAll = []) => {
  return servicePointsAll
    .map(({ name = '', id }) => ({
      text: name.trim(),
      value: id,
    }))
    .sort((a, b) =>
      b.text.localeCompare(a.text, undefined, { sensitivity: 'base' })
    );
};

const handleInsurances = (insurances = []) => {
  return insurances.map(({ id, name }) => ({
    text: name,
    value: id,
  }));
};

const handleRoadsideAssistances = (roadsideAssistances = []) => {
  return roadsideAssistances.map(({ id, name }) => ({
    text: name,
    value: id,
  }));
};

const handleHistory = (history = []) => {
  const mapTypes = {
    [BOARDS_ID.ARM_ACCIDENTS]: { title: 'New Accident', key: 'arnmAccidents' },
    [BOARDS_ID.ARM_REPAIRS]: { title: 'New Repair', key: 'arnmRepairs' },
    [BOARDS_ID.ARM_MAINTENANCE]: {
      title: 'New Maintenance',
      key: 'arnmMaintenance',
    },
  };

  const status = (item) => {
    if (item.type === BOARDS_ID.ARM_ACCIDENTS) {
      return ARNM_ACCIDENTS_TASK_STATUSES_NAMES[item.status]?.label || '';
    }
    if (item.type === BOARDS_ID.ARM_REPAIRS) {
      return ARNM_REPAIRS_TASK_STATUSES_NAMES[item.status]?.label || '';
    }
    if (item.type === BOARDS_ID.ARM_MAINTENANCE) {
      return ARNM_MAINTENANCE_TASK_STATUSES_NAMES[item.status]?.label || '';
    }
    return '';
  };

  return history.map((item) => ({
    ...item,
    field: [mapTypes[item.type]?.title],
    task: {
      id: item.id,
      link: `/${mapTypes[item.type]?.key}/${item.id}`,
      status: status(item),
    },
  }));
};

const handleColors = (data = []) => {
  return data?.map((color) => ({
    label: color?.name,
    text: color?.name,
    value: color?.name,
  }));
};

const handleDealers = (data = []) => {
  return data?.map((dealer) => ({
    label: dealer?.name,
    text: dealer?.name,
    value: dealer?.name,
  }));
};

const handleFinancingProviders = (financingProviders = {}) =>
  Object.keys(financingProviders).map((provider) => {
    return {
      text: financingProviders[provider]?.name,
      value: financingProviders[provider]?.name,
    };
  });

const handleVehicleServicePoints = (data = []) => {
  return data.map((point) => {
    return {
      id: point.servicePoint.id,
      name: point.servicePoint.name || '',
      types: point.servicePoint.supportedWorkTypes || [],
    };
  });
};

const getServicePointsOptions = (servicePointsAll = []) => {
  const formatedPoints = servicePointsAll?.sort((a, b) =>
    a.text.localeCompare(b.text)
  );

  return [...DEFAULT_LOCATIONS, ...formatedPoints];
};

const getPropertiesObject = (vehicle = {}) => {
  return {
    engineDisplacement: {
      label: 'Engine Displacement:',
      renderedValue: vehicle.properties?.engine?.displacement
        ? `${vehicle.properties?.engine?.displacement?.value} ${vehicle.properties?.engine?.displacement?.unitCode}`
        : '-',
    },
    transmission: {
      label: 'Transmission:',
      renderedValue: vehicle.properties?.transmission || '-',
    },
    fuelType: {
      label: 'Fuel Type:',
      renderedValue: vehicle.properties?.fuelType || '-',
    },
    power: {
      label: 'Engine Power:',
      renderedValue: vehicle.properties?.engine?.power
        ? `${vehicle.properties?.engine?.power?.value} ${vehicle.properties?.engine?.power?.unitCode}`
        : '-',
    },
    mileageFromOdometer: {
      label: 'Mileage From Odometer:',
      renderedValue: vehicle.mileageFromOdometer
        ? `${vehicle.mileageFromOdometer?.value} ${vehicle.mileageFromOdometer?.unitCode}`
        : '-',
    },
  };
};

const getProcurementObject = ({
  vehicle = {},
  servicePointsAll,
  availabilityEnums,
  dealers,
  financingProviders,
}) => {
  const servicePointsOptions = getServicePointsOptions(servicePointsAll);

  const location =
    servicePointsOptions.find((sp) => sp.value === vehicle.location?.name)
      ?.text ||
    vehicle.location?.name ||
    '-';
  const availability =
    AVAILABILITY_STATUSES[
      availabilityEnums?.statuses?.find(
        (status) => status?.id === vehicle.availability?.status
      )?.name
    ]?.text || '-';

  return {
    created: {
      sourcingType: {
        canEdited: true,
        label: 'Sourcing Type:',
        type: 'dropdown',
        renderedValue: vehicle.procurement?.sourcingType || '-',
        defaultValue: vehicle.procurement?.sourcingType,
        options: [
          { text: 'OWNED', value: 'OWNED' },
          { text: 'R2R', value: 'R2R' },
          { text: 'BB', value: 'BB' },
        ],
        rules: {},
      },
    },
    order: {
      orderDate: {
        canEdited: true,
        label: 'Order Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.orderDate
          ? formatDate(vehicle.procurement?.orderDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.orderDate,
        rules: {},
      },
      supplierOrderId: {
        canEdited: true,
        label: 'Supplier Order Id:',
        renderedValue: vehicle.procurement?.supplierOrderId || '-',
        defaultValue: vehicle.procurement?.supplierOrderId,
        type: 'text',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      agreedCustomsDate: {
        canEdited: true,
        label: 'Agreed Customs Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.agreedCustomsDate
          ? formatDate(vehicle.procurement?.agreedCustomsDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.agreedCustomsDate,
        rules: {},
      },
      estimatedCustomsDate: {
        canEdited: true,
        label: 'Estimated Customs Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.estimatedCustomsDate
          ? formatDate(vehicle.procurement?.estimatedCustomsDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.estimatedCustomsDate,
        rules: {},
      },
      agreedPurchasePrice: {
        canEdited: true,
        label: 'Agreed Purchase Price:',
        renderedValue: vehicle.procurement?.agreedPurchasePrice?.number
          ? `${vehicle.procurement?.agreedPurchasePrice?.number} ${vehicle.procurement?.agreedPurchasePrice?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.agreedPurchasePrice?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      dealer: {
        canEdited: true,
        label: 'Dealer:',
        renderedValue: vehicle.procurement?.dealer || '-',
        defaultValue: vehicle.procurement?.dealer,
        type: 'dropdown',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
        options: dealers,
      },
    },
    customs: {
      customsEntryDate: {
        canEdited: true,
        label: 'Customs Entry Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.customsEntryDate
          ? formatDate(vehicle.procurement?.customsEntryDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.customsEntryDate,
        rules: {},
      },
    },
    paymentProcess: {
      plannedDownpaymentDate: {
        canEdited: true,
        label: 'Planned Downpayment Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.plannedDownpaymentDate
          ? formatDate(
              vehicle.procurement?.plannedDownpaymentDate,
              'dd/MM/yyyy'
            )
          : '-',
        defaultValue: vehicle.procurement?.plannedDownpaymentDate,
        rules: {},
      },
      delayReason: {
        canEdited: true,
        label: 'Delay Reason:',
        type: 'dropdown',
        renderedValue: vehicle.procurement?.delayReason
          ? DELAY_REASONS.find(
              (reason) => reason.value === vehicle.procurement?.delayReason
            )?.label
          : '-',
        defaultValue: vehicle.procurement?.delayReason,
        options: DELAY_REASONS,
        rules: {},
      },
      vehicleOwnership: {
        canEdited: true,
        label: 'Vehicle Ownership:',
        type: 'dropdown',
        renderedValue: vehicle.procurement?.vehicleOwnership || '-',
        defaultValue: vehicle.procurement?.vehicleOwnership,
        options: VEHICLE_OWNERSHIPS,
        rules: {},
      },
      financingType: {
        canEdited: true,
        label: 'Financing Type:',
        type: 'dropdown',
        renderedValue: vehicle.procurement?.financingType || '-',
        defaultValue: vehicle.procurement?.financingType,
        options: VEHICLE_FINANCING_TYPES,
        rules: {},
      },
      financingProvider: {
        canEdited: true,
        label: 'Financing Provider:',
        type: 'dropdown',
        renderedValue: vehicle.procurement?.financingProvider || '-',
        defaultValue: vehicle.procurement?.financingProvider,
        options: financingProviders,
        rules: {},
      },
      retailPrice: {
        canEdited: true,
        label: 'Retail Price:',
        renderedValue: vehicle.procurement?.retailPrice?.number
          ? `${vehicle.procurement?.retailPrice?.number} ${vehicle.procurement?.retailPrice?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.retailPrice?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      retailPriceBeforeTax: {
        canEdited: true,
        label: 'Retail Price Before Tax:',
        renderedValue: vehicle.procurement?.retailPriceBeforeTax?.number
          ? `${vehicle.procurement?.retailPriceBeforeTax?.number} ${vehicle.procurement?.retailPriceBeforeTax?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.retailPriceBeforeTax?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      vat: {
        canEdited: true,
        label: 'VAT:',
        renderedValue: vehicle.procurement?.vat || '-',
        defaultValue: vehicle.procurement?.vat,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      extras: {
        canEdited: true,
        label: 'Extras:',
        type: 'text',
        renderedValue: vehicle.procurement?.extras?.length
          ? vehicle.procurement?.extras?.join(',')
          : '-',
        defaultValue: vehicle.procurement?.extras?.length
          ? vehicle.procurement?.extras?.join(',')
          : '',
        instructions: 'Comma seperated',
        rules: {},
      },
      extrasPrice: {
        canEdited: true,
        label: 'Extras Price:',
        renderedValue: vehicle.procurement?.extrasPrice?.number
          ? `${vehicle.procurement?.extrasPrice?.number} ${vehicle.procurement?.extrasPrice?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.extrasPrice?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      specialClassificationFee: {
        canEdited: true,
        label: 'Special Classification Fee:',
        renderedValue: vehicle.procurement?.specialClassificationFee?.number
          ? `${vehicle.procurement?.specialClassificationFee?.number} ${vehicle.procurement?.specialClassificationFee?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.specialClassificationFee?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      classificationCosts: {
        canEdited: true,
        label: 'Classification Costs:',
        renderedValue: vehicle.procurement?.classificationCosts?.number
          ? `${vehicle.procurement?.classificationCosts?.number} ${vehicle.procurement?.classificationCosts?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.classificationCosts?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      discountPercentage: {
        canEdited: true,
        label: 'Discount Percentage:',
        renderedValue: vehicle.procurement?.discountPercentage || '-',
        defaultValue: vehicle.procurement?.discountPercentage,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      discountWithoutVat: {
        canEdited: true,
        label: 'Discount Without Vat:',
        renderedValue: vehicle.procurement?.discountWithoutVat?.number
          ? `${vehicle.procurement?.discountWithoutVat?.number} ${vehicle.procurement?.discountWithoutVat?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.discountWithoutVat?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      finalPurchasePrice: {
        canEdited: true,
        label: 'Final Purchase Price:',
        renderedValue: vehicle.procurement?.finalPurchasePrice?.number
          ? `${vehicle.procurement?.finalPurchasePrice?.number} ${vehicle.procurement?.finalPurchasePrice?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.finalPurchasePrice?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      kilometersAtPurchase: {
        canEdited: true,
        label: 'Kilometers At Purchase:',
        renderedValue: vehicle.procurement?.kilometersAtPurchase || '-',
        defaultValue: vehicle.procurement?.kilometersAtPurchase,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      leaseMonthlyInstallment: {
        canEdited: true,
        label: 'Lease Monthly Installment:',
        renderedValue: vehicle.procurement?.leaseMonthlyInstallment?.number
          ? `${vehicle.procurement?.leaseMonthlyInstallment?.number} ${vehicle.procurement?.leaseMonthlyInstallment?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.leaseMonthlyInstallment?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      buyOutPrice: {
        canEdited: true,
        label: 'Buyout Price:',
        renderedValue: vehicle.procurement?.buyOutPrice?.number
          ? `${vehicle.procurement?.buyOutPrice?.number} ${vehicle.procurement?.buyOutPrice?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.buyOutPrice?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      buyOutMonths: {
        canEdited: true,
        label: 'Buyout Months:',
        renderedValue: vehicle.procurement?.buyOutMonths || '-',
        defaultValue: vehicle.procurement?.buyOutMonths,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
    },
    buyBack: {
      buyBackPrice: {
        canEdited: true,
        label: 'Buyback Price:',
        renderedValue: vehicle.procurement?.buyBackPrice?.number
          ? `${vehicle.procurement?.buyBackPrice?.number} ${vehicle.procurement?.buyBackPrice?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.buyBackPrice?.number,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
      buyBackMonths: {
        canEdited: true,
        label: 'Buyback Months:',
        renderedValue: vehicle.procurement?.buyBackMonths || '-',
        defaultValue: vehicle.procurement?.buyBackMonths,
        type: 'number',
        rules: {},
        fieldWrapperStyle: {
          width: '100%',
        },
      },
    },
    dealerPreparation: {
      licenseIssuanceDate: {
        canEdited: true,
        label: 'License Issuance Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.licenseIssuanceDate
          ? formatDate(vehicle.procurement?.licenseIssuanceDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.licenseIssuanceDate,
        rules: {},
      },
      insuranceProvider: {
        canEdited: true,
        label: 'Insurance Provider:',
        type: 'text',
        renderedValue: vehicle.procurement?.insuranceProvider || '-',
        defaultValue: vehicle.procurement?.insuranceProvider,
        rules: {},
      },
      insuranceDate: {
        canEdited: true,
        label: 'Insurance Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.insuranceDate
          ? formatDate(vehicle.procurement?.insuranceDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.insuranceDate,
        rules: {},
      },
      vehiclePurchaseDate: {
        canEdited: true,
        label: 'Vehicle Purchase Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.vehiclePurchaseDate
          ? formatDate(vehicle.procurement?.vehiclePurchaseDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.vehiclePurchaseDate,
        rules: {},
      },
      leaseStartDate: {
        canEdited: true,
        label: 'Lease Start Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.leaseStartDate
          ? formatDate(vehicle.procurement?.leaseStartDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.leaseStartDate,
        rules: {},
      },
      estimatedCarReceiptDate: {
        canEdited: true,
        label: 'Estimated Car Receipt Date:',
        type: 'date',
        disabled: true,
        renderedValue: vehicle.procurement?.estimatedCarReceiptDate
          ? formatDate(
              vehicle.procurement?.estimatedCarReceiptDate,
              'dd/MM/yyyy'
            )
          : '-',
        defaultValue: vehicle.procurement?.estimatedCarReceiptDate,
        rules: {},
      },
      plannedReceiptDate: {
        canEdited: true,
        label: 'Planned Receipt Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.plannedReceiptDate
          ? formatDate(vehicle.procurement?.plannedReceiptDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.plannedReceiptDate,
        rules: {},
      },
      receiptDate: {
        canEdited: true,
        label: 'Receipt Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.receiptDate
          ? formatDate(vehicle.procurement?.receiptDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.receiptDate,
        rules: {},
      },
    },
    fleet: {
      location: {
        canEdited: true,
        label: 'Location:',
        type: 'dropdown',
        renderedValue: location,
        defaultValue: vehicle.location?.name,
        rules: {},
        options: servicePointsOptions,
      },
      availabilityStatus: {
        label: 'Availability Status:',
        type: 'text',
        renderedValue: availability,
        defaultValue: availability,
        rules: {},
        disabled: true,
      },
      reservationType: {
        label: 'Reservation Type:',
        type: 'text',
        renderedValue:
          RESERVATIONS_TYPES[
            availabilityEnums?.reservationsTypes?.find(
              (type) => type?.id === vehicle.availability?.reservationType
            )?.name
          ]?.text || '-',
        defaultValue:
          RESERVATIONS_TYPES[
            availabilityEnums?.reservationsTypes?.find(
              (type) => type?.id === vehicle.availability?.reservationType
            )?.name
          ]?.text || '-',
        rules: {},
        disabled: true,
      },
    },
    defleeted: {
      defleetPriority: {
        title: 'Update Defleet Priority',
        label: 'Defleet Priority:',
        type: 'dropdown',
        placeholder: '',
        canEdited: true,
        onEditField: null,
        options: DEFLEETED_PRIORITIES,
        renderedValue: vehicle.procurement?.defleetPriority
          ? DEFLEETED_PRIORITIES.find(
              (priority) =>
                priority.value === String(vehicle.procurement?.defleetPriority)
            )?.label
          : '-',
        defaultValue: String(vehicle.procurement?.defleetPriority),
        rules: {},
      },
      monthOfDefleet: {
        title: 'Update Month of Defleet',
        label: 'Month of Defleet:',
        type: 'date',
        placeholder: '',
        canEdited: true,
        onEditField: null,
        renderedValue: vehicle.procurement?.monthOfDefleet
          ? formatDate(vehicle.procurement?.monthOfDefleet, 'MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.monthOfDefleet,
        rules: {},
      },

      defleetedDate: {
        canEdited: false,
        label: 'Defleeted Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.defleetedDate
          ? formatDate(vehicle.procurement?.defleetedDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.defleetedDate,
        rules: {},
      },
      defleetedReason: {
        canEdited: false,
        label: 'Reason:',
        type: 'textArea',
        renderedValue: vehicle.procurement?.cancelledDefleetedReason || '-',
        rules: {},
      },
      insuranceCancellationDate: {
        title: 'Update Insurance Cancellation Date',
        label: 'Insurance Cancellation Date:',
        type: 'date',
        placeholder: '',
        canEdited: true,
        onEditField: null,
        renderedValue: vehicle.procurement?.insuranceCancellationDate
          ? formatDate(
              vehicle.procurement?.insuranceCancellationDate,
              'dd/MM/yyyy'
            )
          : '-',
        defaultValue: vehicle.procurement?.insuranceCancellationDate,
        rules: {},
      },
      transferCost: {
        canEdited: false,
        label: 'Transfer Cost:',
        type: 'number',
        renderedValue: vehicle.procurement?.transferCost?.number
          ? `${vehicle.procurement?.transferCost?.number} ${vehicle.procurement?.transferCost?.currency}`
          : '-',
        defaultValue: vehicle.procurement?.transferCost?.number,
        rules: {},
      },
    },
    canceled: {
      cancelledDate: {
        canEdited: true,
        label: 'Cancelled Date:',
        type: 'date',
        renderedValue: vehicle.procurement?.cancelledDate
          ? formatDate(vehicle.procurement?.cancelledDate, 'dd/MM/yyyy')
          : '-',
        defaultValue: vehicle.procurement?.cancelledDate,
        rules: {},
      },
    },
  };
};

const getInvoiceDetailsObject = (vehicle = {}) => {
  return {
    invoiceNumber: {
      label: 'Invoice Number',
      defaultValue: vehicle.invoiceDetails?.invoiceNumber,
      type: 'text',
      renderedValue: vehicle.invoiceDetails?.invoiceNumber || '-',
    },
    netPrice: {
      label: 'Net Price:',
      defaultValue: vehicle.invoiceDetails?.netPrice?.number,
      type: 'number',
      renderedValue: vehicle.invoiceDetails?.netPrice
        ? `${vehicle.invoiceDetails?.netPrice?.number} ${vehicle.invoiceDetails?.netPrice?.currency}`
        : '-',
    },
    registrationTax: {
      label: 'Registration Tax:',
      defaultValue: vehicle.invoiceDetails?.registrationTax?.number,
      type: 'number',
      renderedValue: vehicle.invoiceDetails?.registrationTax
        ? `${vehicle.invoiceDetails?.registrationTax?.number} ${vehicle.invoiceDetails?.registrationTax?.currency}`
        : '-',
    },
    vat: {
      label: 'Vat:',
      defaultValue: vehicle.invoiceDetails?.vat?.number,
      type: 'number',
      renderedValue: vehicle.invoiceDetails?.vat
        ? `${vehicle.invoiceDetails?.vat?.number} ${vehicle.invoiceDetails?.vat?.currency}`
        : '-',
    },
    transferFee: {
      label: 'Transfer Fee:',
      defaultValue: vehicle.invoiceDetails?.transferFee?.number,
      type: 'number',
      renderedValue: vehicle.invoiceDetails?.transferFee
        ? `${vehicle.invoiceDetails?.transferFee?.number} ${vehicle.invoiceDetails?.transferFee?.currency}`
        : '-',
    },
    plateReleaseFee: {
      label: 'PlateRelease Fee:',
      defaultValue: vehicle.invoiceDetails?.plateReleaseFee?.number,
      type: 'number',
      renderedValue: vehicle.invoiceDetails?.plateReleaseFee
        ? `${vehicle.invoiceDetails?.plateReleaseFee?.number} ${vehicle.invoiceDetails?.plateReleaseFee?.currency}`
        : '-',
    },
  };
};

const findDocument = (documents, id) => {
  return documents?.find((item) => {
    return item?.id === id;
  });
};

const getDetailsObject = (
  vehicle = {},
  insurances = [],
  roadsideAssistances = []
) => {
  return {
    createdAt: {
      label: 'Created At:',
      defaultValue: vehicle.createdAt,
      type: 'date',
      disabled: true,
      renderedValue: vehicle.createdAt
        ? formatDate(vehicle.createdAt, 'dd/MM/yyyy')
        : '-',
    },
    updatedAt: {
      label: 'Updated At:',
      defaultValue: vehicle.updatedAt,
      disabled: true,
      type: 'date',
      renderedValue: vehicle.updatedAt
        ? formatDate(vehicle.updatedAt, 'dd/MM/yyyy')
        : '-',
    },
    insuranceId: {
      title: 'Update Insurance',
      label: 'Insurance:',
      type: 'dropdown',
      placeholder: '',
      canEdited: true,
      renderedValue: vehicle.insurance?.name || '-',
      defaultValue: vehicle.insurance?.id,
      options: insurances,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    roadsideAssistanceId: {
      title: 'Update Roadside',
      label: 'Roadside:',
      type: 'dropdown',
      placeholder: '',
      canEdited: true,
      renderedValue: vehicle.roadsideAssistance?.name || '-',
      defaultValue: vehicle.roadsideAssistance?.id,
      options: roadsideAssistances,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    insuranceRenewalDate: {
      title: 'Update Insurance Renewal Date',
      label: 'Insurance Renewal Date:',
      type: 'date',
      placeholder: '',
      canEdited: false,
      renderedValue: vehicle.procurement?.insuranceRenewalDate
        ? formatDate(vehicle.procurement?.insuranceRenewalDate, 'dd/MM/yyyy')
        : '-',
      defaultValue: vehicle.procurement?.insuranceRenewalDate,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    activeLicense: {
      title: 'Active License',
      label: 'Active License:',
      type: 'dropdown',
      placeholder: '',
      canEdited: false,
      renderedValue: vehicle.activeLicense
        ? findDocument(vehicle.documents, vehicle.activeLicense)?.filename
        : '-',
      defaultValue: vehicle.activeLicense,
      options: vehicle.documents?.map((item) => {
        return {
          text: `${item?.type} - ${item?.filename}`,
          value: item?.id,
        };
      }),
      link: findDocument(vehicle.documents, vehicle.activeLicense)?.url,
      target: '_blank',
      onEditField: null,
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    activeInsurance: {
      title: 'Active Insurance',
      label: 'Active Insurance:',
      type: 'dropdown',
      placeholder: '',
      canEdited: false,
      renderedValue: vehicle.activeInsurance
        ? findDocument(vehicle.documents, vehicle.activeInsurance)?.filename
        : '-',
      defaultValue: vehicle.activeInsurance,
      options: vehicle.documents?.map((item) => {
        return {
          text: `${item?.type} - ${item?.filename}`,
          value: item?.id,
        };
      }),
      link: findDocument(vehicle.documents, vehicle.activeInsurance)?.url,
      target: '_blank',
      onEditField: null,
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    activeKteo: {
      title: 'Active KTEO',
      label: 'Active KTEO:',
      type: 'dropdown',
      placeholder: '',
      canEdited: false,
      renderedValue: vehicle.activeKteo
        ? findDocument(vehicle.documents, vehicle.activeKteo)?.filename
        : '-',
      link: findDocument(vehicle.documents, vehicle.activeKteo)?.url,
      target: '_blank',
      defaultValue: vehicle.activeKteo,
      options: vehicle.documents?.map((item) => {
        return {
          text: `${item?.type} - ${item?.filename}`,
          value: item?.id,
        };
      }),
      onEditField: null,
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    multimediaPin: {
      label: 'Multimedia PIN:',
      title: 'Update Multimedia PIN',
      type: 'text',
      canEdited: true,
      placeholder: '',
      renderedValue: vehicle.multimediaPin || '-',
      defaultValue: vehicle.multimediaPin,
      onEditField: null,
      hasCopyValueButton: true,
      fieldWrapperStyle: {
        width: '100%',
      },
    },
  };
};

const getInformationObject = (
  vehicle = {},
  colors = [],
  categoryClasses = {}
) => {
  return {
    skuId: {
      title: 'Update SKU',
      label: 'SKU:',
      type: 'text',
      canEdited: true,
      placeholder: '',
      renderedValue: vehicle.skuId || '-',
      defaultValue: vehicle.skuId,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    plate: {
      label: 'Plate:',
      renderedValue: vehicle.plate || '-',
    },
    manufacturer: {
      label: 'Manufacturer:',
      renderedValue: vehicle.manufacturer || '-',
    },
    model: {
      label: 'Model:',
      renderedValue: vehicle.model || '-',
    },
    edition: {
      label: 'Edition:',
      renderedValue: vehicle.sku?.edition || '-',
    },
    year: {
      label: 'Year:',
      renderedValue: vehicle.sku?.year || '-',
    },
    vin: {
      title: 'Edit VIN (identification number)',
      label: 'VIN:',
      type: 'text',
      canEdited: true,
      placeholder: '',
      renderedValue: vehicle.vin || '-',
      defaultValue: vehicle.vin,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    color: {
      title: 'Update Color',
      label: 'Color:',
      type: 'autocomplete',
      withAddOption: true,
      canEdited: true,
      placeholder: '',
      renderedValue: vehicle.properties?.color || '-',
      defaultValue: vehicle.properties?.color,
      options: colors,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    condition: {
      label: 'Condition:',
      title: 'Update Condition',
      type: 'dropdown',
      placeholder: '',
      canEdited: true,
      renderedValue: vehicle.condition || '-',
      defaultValue: vehicle.condition,
      options: [
        { text: 'NEW', value: 'NEW' },
        { text: 'USED', value: 'USED' },
      ],
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    description: {
      label: 'Description',
      title: 'Update Description:',
      type: 'text',
      canEdited: true,
      placeholder: '',
      renderedValue: vehicle.description || '-',
      defaultValue: vehicle.description,
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    categoryClass: {
      label: 'Category Class',
      type: 'text',
      canEdited: false,
      placeholder: '',
      renderedValue: categoryClasses[vehicle?.sku?.categoryClass]?.name || '-',
      onEditField: null,
      fieldWrapperStyle: {
        width: '100%',
      },
    },
  };
};

const handleVehicle = ({
  vehicle = {},
  servicePointsAll,
  availabilityEnums,
  roadsideAssistances,
  insurances,
  financingProviders,
  colors,
  dealers,
}) => {
  const data = {
    id: vehicle.id,
    renderId: generateShortId(),
    images: vehicle.images || [],
    title: `${vehicle.manufacturer || ''}${
      vehicle.model ? ` - ${vehicle.model}` : ''
    }`,
    internalId: vehicle.internalId || '-',
    plate: vehicle.plate || '',
    initialPrice: vehicle.sku?.initialPrice ?? '',
    monthlyPrice: vehicle.sku?.price ?? '',
    bodyType: vehicle.sku?.body?.type ?? '',
    pricing:
      size(vehicle.pricing || []) === 0
        ? VEHICLE_DEFAULT_PRICING
        : vehicle.pricing,
    type: vehicle.sku?.type || '',
    stage: {
      newStageId: {
        title: 'Update Stage',
        label: 'Stage:',
        canEdited: false,
        renderedValue: formatVehicleStage(vehicle.procurement?.currentStageId),
        type: 'dropdown',
        placeholder: '',
        defaultValue: vehicle.procurement?.currentStageId,
        options: [],
        onEditField: null,
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
      },
    },
    information: getInformationObject(
      vehicle,
      colors,
      availabilityEnums?.categoryClasses
    ),
    details: getDetailsObject(vehicle, insurances, roadsideAssistances),
    color: vehicle.properties?.color,
    dealers: [],
    financingProviders: [],
    properties: getPropertiesObject(vehicle),
    procurement: getProcurementObject({
      vehicle,
      servicePointsAll,
      availabilityEnums,
      dealers,
      financingProviders,
    }),
    services: vehicle.services?.map((service) => {
      return {
        id: service.id,
        createdAt: service.createdAt ? formatDate(service.createdAt) : '-',
        updatedAt: service.updatedAt ? formatDate(service.updatedAt) : '-',
        vehicleServiceID: service.vehicleServiceID || '-',
      };
    }),
    costs: vehicle.costs?.map((cost) => {
      return {
        id: cost.id,
        createdAt: cost.createdAt ? formatDate(cost.createdAt) : '-',
        updatedAt: cost.updatedAt ? formatDate(cost.updatedAt) : '-',
        type: cost.type || '-',
        amount: cost.amount ? formatAmount(cost.amount.value) : '-',
        comment: cost.comment || '-',
      };
    }),
    insurances: vehicle.insurances?.map((insurance) => {
      return {
        id: insurance.id,
        amount: insurance.amount ? formatAmount(insurance.amount.value) : '-',
        comment: insurance.comment || '-',
        completedAt: insurance.completedAt
          ? formatDate(insurance.completedAt)
          : '-',
        createdAt: insurance.createdAt ? formatDate(insurance.createdAt) : '-',
        startedAt: insurance.startedAt ? formatDate(insurance.startedAt) : '-',
        updatedAt: insurance.updatedAt ? formatDate(insurance.updatedAt) : '-',
      };
    }),
    documents:
      vehicle.documents?.map((document) => {
        return {
          id: document.id,
          createdAt: document.createdAt
            ? formatDate(document.createdAt, 'dd/MM/yyyy')
            : '-',
          filename: document.filename,
          type: document.type,
          url: document.url,
        };
      }) || [],
    servicePoints: [],
    existingServicePointsIds: [],
    servicePointsAll: [],
    invoiceDetails: getInvoiceDetailsObject(vehicle),
  };

  return data;
};

export const getBulkUpdateVehiclesPayload = (changes) => {
  return changes.reduce((acc, curr) => {
    const { id, key, text, type, value } = curr;
    let change = {};
    if (key === 'location') {
      change = {
        location: {
          location: text,
        },
      };
    }
    if (topLevelFields.includes(key)) {
      change = {
        [key]: key === 'tenant' ? Number(text) : text,
      };
    }
    if (propertiesFields.includes(key)) {
      change = {
        properties: {
          [key]: text,
        },
      };
    }
    if (invoiceDetailsFields.includes(key)) {
      change = {
        invoiceDetails: {
          [key === 'invoiceDetailsVat' ? 'vat' : key]: text?.toString(),
        },
      };
    }
    if (procurementFields.includes(key)) {
      let newValue = text;

      if (type === 'date') {
        newValue = value ? formatDate(value, 'yyyy-MM-dd') : undefined;
      }

      if (type === 'number') {
        newValue = value != null && !isNaN(value) ? String(value) : undefined;
      }
      if (key === 'defleetPriority') {
        newValue = Number(text);
      }
      change = {
        procurement: {
          [key]: newValue,
        },
      };
    }
    acc.push({
      id,
      ...change,
    });
    return acc;
  }, []);
};

const BOARD_ID_VALUE_TO_KEY = Object.entries(BOARDS_ID).reduce(
  (acc, [key, val]) => {
    acc[val] = key;
    return acc;
  },
  {}
);

const BOARD_META_BY_ID = Object.values(BOARDS_ENUMS).reduce((acc, board) => {
  if (!board?.board) return acc;

  acc[board.board] = {
    name: board.text,
    url: BOARDS_ID_TO_URLS_MAP[BOARD_ID_VALUE_TO_KEY[board.board]] || null,
  };

  return acc;
}, {});

const handleInfiniteQueryData = (
  data,
  servicePoints,
  insurances,
  roadsideAssistance,
  loggedInUser,
  getAssigneeName
) => {
  if (!data || !data.length) return [];

  return data.map((item) => {
    const type = item?.type;

    if (type === AVAILABLE_HISTORY_ITEMS.task) {
      const boardType = item?.data?.boardType;

      const boardMeta = BOARD_META_BY_ID[boardType] || {};

      let ticketNumber = item?.data?.ticketNumber || '';

      if (
        boardType === BOARDS_ID.INSTATRADES ||
        boardType === BOARDS_ID.SERVICE_INBOX ||
        boardType === BOARDS_ID.SUBSCRIPTIONS
      ) {
        ticketNumber = item?.data?.resourceId || '';
      }

      const ticketUrl =
        boardMeta.url && ticketNumber
          ? `${boardMeta.url}/${ticketNumber}`
          : null;

      const userId = item?.data?.userId || '';

      const userUrl = `${URLS.users}/${userId || ''}`;

      const assigneeEmail = item?.data?.assignee || '';

      const assignee = getAssigneeName(assigneeEmail);
      return {
        type,
        ...(item.data || {}),
        board: boardMeta.name || boardType,
        url: ticketUrl,
        userUrl,
        assignee,
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.changelog) {
      const itemData = item?.data || {};

      const changelogItem = handleChangelogItemFieldName(
        itemData,
        servicePoints,
        insurances,
        roadsideAssistance
      );

      const changelogActor = changelogItem?.actor || '';

      const actor = isSystemActor(changelogActor)
        ? changelogActor
        : getAssigneeName(changelogActor);

      return {
        type,
        ...itemData,
        ...(changelogItem || {}),
        actor,
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.comment) {
      const itemData = item?.data || {};
      return {
        type,
        ...itemData,
        isLoggedInUser: itemData?.author === loggedInUser?.email,
      };
    }

    return {
      type,
      ...(item.data || {}),
    };
  });
};

const handleFleetVehiclesComments = (data, loggedInUser, getAssigneeName) => {
  if (!data?.length > 0) return [];

  return (
    data?.map((item) => {
      return {
        id: item.id,
        type: 'comment',
        comment: item.comment,
        createdAt: item.createdAt,
        createdBy: item.author,
        userName: getAssigneeName(item.author),
        author: {
          email: item.author,
        },
        isLoggedInUser: item.author === loggedInUser?.email,
      };
    }) || []
  );
};

const parseDateRangeFilterValue = (dateValue) => {
  if (!dateValue) return [null, null];

  if (Array.isArray(dateValue)) {
    return [dateValue[0] || null, dateValue[1] || null];
  }

  if (typeof dateValue === 'string') {
    const [start, end] = dateValue.split(',');

    if (end === undefined) {
      return [start || null, start || null];
    }

    return [start || null, end || null];
  }

  return [dateValue, dateValue];
};

const toAthensDayStartIso = (dateValue) => {
  if (!dateValue) return null;

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return null;

  // Vehicles are saved with Athens timezone. Use 03:00:00
  // so day-based comparisons match the stored representation.
  parsed.setHours(3, 0, 0, 0);
  return parsed.toISOString();
};

const getDateFilterRule = (field, dateValue) => {
  const [start, end] = parseDateRangeFilterValue(dateValue);
  const startDate = toAthensDayStartIso(start);
  const endDate = toAthensDayStartIso(end);

  if (!startDate && !endDate) return undefined;

  const value = [];

  if (startDate) {
    value.push({
      type: FILTER_RULE_ENUMS.CompareDateRuleType,
      field,
      operator: FILTER_OPERATOR_ENUMS['>='],
      value: computeValueByRuleType(
        FILTER_RULE_ENUMS.CompareDateRuleType,
        FILTER_FIELDS_TYPES.Date,
        startDate
      ),
    });
  }

  if (endDate) {
    value.push({
      type: FILTER_RULE_ENUMS.CompareDateRuleType,
      field,
      operator: FILTER_OPERATOR_ENUMS['<='],
      value: computeValueByRuleType(
        FILTER_RULE_ENUMS.CompareDateRuleType,
        FILTER_FIELDS_TYPES.Date,
        endDate
      ),
    });
  }

  return {
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value,
  };
};

const getStageFilterRule = (procurementStageIds, field, payloadStageValue) => {
  if (payloadStageValue === FLEET_VEHICLES_TABS.procurement.key) {
    const procurementStageId = procurementStageIds.find(
      (id) => id.text === FLEET_VEHICLES_TABS.procurement.label
    )?.value;

    if (!procurementStageId && procurementStageId !== 0) return undefined;

    return {
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      field,
      operator: FILTER_OPERATOR_ENUMS['<'],
      value: Number(procurementStageId),
    };
  }

  if (payloadStageValue === FLEET_VEHICLES_TABS.fleet.key) {
    const procurementStageId = procurementStageIds.find(
      (id) => id.text === FLEET_VEHICLES_TABS.procurement.label
    )?.value;

    if (!procurementStageId && procurementStageId !== 0) return undefined;

    return {
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      field,
      operator: FILTER_OPERATOR_ENUMS['='],
      value: Number(procurementStageId),
    };
  }

  const tabsArray = Object.values(FLEET_VEHICLES_TABS);

  const selectedTab = tabsArray.find((tab) => tab.key === payloadStageValue);

  const stageId = procurementStageIds.find(
    (id) => id.text === selectedTab?.label
  )?.value;

  if (!stageId && stageId !== 0) return undefined;

  return {
    type: FILTER_RULE_ENUMS.ComparableRuleType,
    field,
    operator: FILTER_OPERATOR_ENUMS['='],
    value: Number(stageId),
  };
};

const getFleetVehiclesRequestPayload = (
  payload = {},
  procurementStageIds = []
) => {
  const numberFields = ['tenant'];
  const multiFilterStringFields = ['categoryClass'];
  const multiFilterNumberFields = [];
  const fields = pick(payload, 'categoryClass', 'tenant');
  const fieldsMap = {
    categoryClass: 'sku.properties.categoryClass',
    plannedDownpaymentDate: 'procurement.plannedDownpaymentDate',
    licenseIssuanceDate: 'procurement.licenseIssuanceDate',
    plannedReceiptDate: 'procurement.plannedReceiptDate',
    receiptDate: 'procurement.receiptDate',
    stage: 'procurement.current_stage_id',
  };

  const _getValue = ({ field }) => {
    if (multiFilterStringFields.includes(field)) {
      return payload[field]?.split(',');
    }
    if (multiFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }

    return numberFields.includes(field)
      ? Number(payload[field])
      : payload[field];
  };
  const _getType = ({ field }) => {
    if (
      multiFilterStringFields.includes(field) ||
      multiFilterNumberFields.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }

    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const value = [
    ...Object.keys(fields).map((field) => ({
      field: fieldsMap[field] || field,
      operator: FILTER_OPERATOR_ENUMS['='],
      type: _getType({ field }),
      value: _getValue({ field }),
    })),
  ];

  const plannedReceiptDateRule = getDateFilterRule(
    fieldsMap?.plannedReceiptDate,
    payload?.plannedReceiptDate
  );
  const licenseIssuanceDateRule = getDateFilterRule(
    fieldsMap?.licenseIssuanceDate,
    payload?.licenseIssuanceDate
  );
  const plannedDownpaymentDateRule = getDateFilterRule(
    fieldsMap?.plannedDownpaymentDate,
    payload?.plannedDownpaymentDate
  );
  const receiptDateRule = getDateFilterRule(
    fieldsMap?.receiptDate,
    payload?.receiptDate
  );
  const stageFilterRule = getStageFilterRule(
    procurementStageIds,
    fieldsMap?.stage,
    payload?.stage
  );

  if (plannedReceiptDateRule) value.push(plannedReceiptDateRule);
  if (licenseIssuanceDateRule) value.push(licenseIssuanceDateRule);
  if (plannedDownpaymentDateRule) value.push(plannedDownpaymentDateRule);
  if (receiptDateRule) value.push(receiptDateRule);
  if (stageFilterRule) value.push(stageFilterRule);

  if (value.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};

const getParamsAndPayloadFleetVehicles = ({
  orderBy,
  order,
  page,
  pageSize,
  tenant,
  stage,
  query,
  plannedReceiptDate,
  licenseIssuanceDate,
  plannedDownpaymentDate,
  receiptDate,
  categoryClass,
  procurementStageIds,
}) => {
  const cleanedParams = getCleanedUrlParams({
    orderBy,
    order,
    page,
    pageSize: parseInt(pageSize, 10) || VEHICLES_PAGE_SIZE,
    query,
  });

  const filtersPayload = {
    plannedReceiptDate,
    licenseIssuanceDate,
    plannedDownpaymentDate,
    receiptDate,
    tenant,
    stage,
  };
  if (categoryClass) {
    filtersPayload.categoryClass = categoryClass;
  }
  const payload = getFleetVehiclesRequestPayload(
    getCleanedUrlParams(filtersPayload),
    procurementStageIds
  );

  return {
    params: cleanedParams,
    payload,
  };
};

export {
  handleChangeLog,
  handleServicePointsAll,
  normalizeColors,
  normalizeDealers,
  normalizeServicePoints,
  normalizeVehicleSourcingTypes,
  getServicePointsMap,
  handleInsurances,
  handleRoadsideAssistances,
  handleHistory,
  handleColors,
  handleDealers,
  handleFinancingProviders,
  handleVehicleServicePoints,
  handleVehicle,
  handleInfiniteQueryData,
  handleFleetVehiclesComments,
  getFleetVehiclesRequestPayload,
  getParamsAndPayloadFleetVehicles,
};
